import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { UserCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { RoleCreate } from "../../../role/components/create/RoleCreate";
import { RoleCreateStore } from "../../../role/store";
import { Language } from "../../../../api/graphql";
import { AvatarField } from "../../../common/controls";
import { TenantCreate } from "../../../tenant/components/create/TenantCreate";
import { TenantCreateStore } from "../../../tenant/store";
import { InputBoolean } from "../../../common/controls";
import { InputPhone } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { IdCardType } from "../../../../api/graphql";
import { InputStringMask } from "../../../common/controls";
import { Gender } from "../../../../api/graphql";

export type UserCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  email: string;
  emailError?: string;
  roleSelectedValue?: { displayId: string; id: string } | null;
  onRoleSelected?: (value: { displayId: string; id: string } | null) => void;
  onRoleChange?: (value: string) => void;
  roleOptions?: { displayId: string; id: string }[];
  roleError?: string;
  languageError?: string;
  languageOptions: { id: string; displayId: string }[];
  language: Language;
  onLanguageChange?: (value: Language) => void;
  onAvatarChange: (blob: Blob) => void;
  onAvatarDelete: () => void;
  targetTenantSelectedValue?: { displayId: string; id: string } | null;
  onTargetTenantSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onTargetTenantChange?: (value: string) => void;
  targetTenantOptions?: { displayId: string; id: string }[];
  targetTenantError?: string;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  active: boolean;
  activeError?: string;
  cellphoneSelectedValue?: { displayId: string; id: string } | null;
  onCellphoneChange?: (value: string) => void;
  cellphone: string;
  cellphoneError?: string;
  twoFaseAuthSelectedValue?: { displayId: string; id: string } | null;
  onTwoFaseAuthChange?: (value: boolean) => void;
  twoFaseAuth: boolean;
  twoFaseAuthError?: string;
  idCardTypeError?: string;
  idCardTypeOptions: { id: string; displayId: string }[];
  idCardType: IdCardType | "";
  onIdCardTypeChange?: (value: IdCardType | "") => void;
  idCardSelectedValue?: { displayId: string; id: string } | null;
  onIdCardChange?: (value: string) => void;
  idCard: string;
  idCardError?: string;
  genderError?: string;
  genderOptions: { id: string; displayId: string }[];
  gender: Gender | "";
  onGenderChange?: (value: Gender | "") => void;
  userCreateStore: UserCreateStore;
};

export const UserCreateForm: React.FC<UserCreateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    name = "",
    nameError,
    onNameChange = () => {},
    email = "",
    emailError,
    onEmailChange = () => {},
    onRoleSelected = () => {},
    roleSelectedValue = null,
    roleOptions = [],
    roleError,
    onRoleChange = () => {},
    languageError,
    languageOptions = [],
    language = "" as Language,
    onLanguageChange = () => {},
    onAvatarChange,
    onAvatarDelete,
    onTargetTenantSelected = () => {},
    targetTenantSelectedValue = null,
    targetTenantOptions = [],
    targetTenantError,
    onTargetTenantChange = () => {},
    active = false,
    activeError,
    onActiveChange = () => {},
    cellphone = "",
    cellphoneError,
    onCellphoneChange = () => {},
    twoFaseAuth = false,
    twoFaseAuthError,
    onTwoFaseAuthChange = () => {},
    idCardTypeError,
    idCardTypeOptions = [],
    idCardType = "" as IdCardType | "",
    onIdCardTypeChange = () => {},
    idCard = "",
    idCardError,
    onIdCardChange = () => {},
    genderError,
    genderOptions = [],
    gender = "" as Gender | "",
    onGenderChange = () => {},
    userCreateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="User"
            mode="create"
            values={{}}
            options={{ role: roleOptions, targetTenant: targetTenantOptions }}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "name":
                  onNameChange?.(valor);
                  break;
                case "email":
                  onEmailChange?.(valor);
                  break;
                case "role":
                  onRoleSelected?.(valor);
                  break;
                case "language":
                  onLanguageChange?.(valor);
                  break;
                case "targetTenant":
                  onTargetTenantSelected?.(valor);
                  break;
                case "active":
                  onActiveChange?.(valor);
                  break;
                case "cellphone":
                  onCellphoneChange?.(valor);
                  break;
                case "twoFaseAuth":
                  onTwoFaseAuthChange?.(valor);
                  break;
                case "idCardType":
                  onIdCardTypeChange?.(valor);
                  break;
                case "idCard":
                  onIdCardChange?.(valor);
                  break;
                case "gender":
                  onGenderChange?.(valor);
                  break;
              }
            }}
          />

          <GroupContainer
            id="main"
            errorText={userCreateStore.getCardErrors("main")}
            title={langStore.groupUserMainLabel}
            collapsible={true}
            expanded={userCreateStore.getCardState("main")}
            toggleExpand={() => userCreateStore.toggleCardState("main")}
          >
            <FormControl fullWidth>
              <InputString
                autoFocus
                value={name}
                error={Boolean(nameError)}
                warning={
                  !nameError && Boolean(userCreateStore.warnings["name"])
                }
                helperText={
                  nameError || userCreateStore.warnings["name"]?.message
                }
                label={langStore.UserNameLabel}
                onChange={(value: string) => onNameChange(value)}
                description={langStore.UserNameDescription}
              />

              <InputString
                value={email}
                error={Boolean(emailError)}
                warning={
                  !emailError && Boolean(userCreateStore.warnings["email"])
                }
                helperText={
                  emailError || userCreateStore.warnings["email"]?.message
                }
                label={langStore.UserEmailLabel}
                onChange={(value: string) => onEmailChange(value)}
                description={langStore.UserEmailDescription}
              />
              <InputReference
                id="role"
                label={langStore.UserRoleLabel}
                error={Boolean(roleError)}
                helperText={
                  roleError || userCreateStore.warnings["role"]?.message
                }
                selectedValue={roleSelectedValue}
                onChange={(value: string) => onRoleChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onRoleSelected(value)
                }
                options={roleOptions}
                description={langStore.UserRoleDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions("role_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) => new RoleCreateStore(rootStore)}
                    renderForm={(store) => (
                      <RoleCreate
                        roleCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewRole"
                    cancelMethod="cancelNewRole"
                  />
                )}
              />
              <InputReference
                id="language"
                error={Boolean(languageError)}
                helperText={
                  languageError || userCreateStore.warnings["language"]?.message
                }
                label={langStore.UserLanguageLabel}
                options={languageOptions}
                selectedValue={
                  language
                    ? {
                        id: language,
                        displayId:
                          languageOptions.find(
                            (o: { id: string }) => o.id === language
                          )?.displayId || language,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onLanguageChange((value?.id as Language) || ("" as Language))
                }
              />
              <AvatarField
                label={langStore.UserAvatarLabel}
                onAvatarChange={(blob: Blob) => onAvatarChange(blob)}
                onAvatarDelete={() => onAvatarDelete()}
              />
              <InputReference
                id="targetTenant"
                label={langStore.UserTargetTenantLabel}
                error={Boolean(targetTenantError)}
                helperText={
                  targetTenantError ||
                  userCreateStore.warnings["targetTenant"]?.message
                }
                selectedValue={targetTenantSelectedValue}
                onChange={(value: string) => onTargetTenantChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onTargetTenantSelected(value)
                }
                options={targetTenantOptions}
                description={langStore.UserTargetTenantDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions("tenant_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new TenantCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <TenantCreate
                        tenantCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewTenant"
                    cancelMethod="cancelNewTenant"
                  />
                )}
              />
              <InputBoolean
                value={active}
                error={Boolean(activeError)}
                helperText={
                  activeError || userCreateStore.warnings["active"]?.message
                }
                label={langStore.UserActiveLabel}
                description={langStore.UserActiveDescription}
                onChange={(value: boolean) => onActiveChange(value)}
              />

              <InputPhone
                value={cellphone}
                error={Boolean(cellphoneError)}
                helperText={
                  cellphoneError ||
                  userCreateStore.warnings["cellphone"]?.message
                }
                label={langStore.UserCellphoneLabel}
                onChange={(value: string) => onCellphoneChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.UserCellphoneDescription}
              />
              <InputBoolean
                value={twoFaseAuth}
                error={Boolean(twoFaseAuthError)}
                helperText={
                  twoFaseAuthError ||
                  userCreateStore.warnings["twoFaseAuth"]?.message
                }
                label={langStore.UserTwoFaseAuthLabel}
                description={langStore.UserTwoFaseAuthDescription}
                onChange={(value: boolean) => onTwoFaseAuthChange(value)}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="details"
            errorText={userCreateStore.getCardErrors("details")}
            title={langStore.groupUserDetailsLabel}
            collapsible={true}
            expanded={userCreateStore.getCardState("details")}
            toggleExpand={() => userCreateStore.toggleCardState("details")}
          >
            <FormControl fullWidth>
              <InputReference
                id="idCardType"
                error={Boolean(idCardTypeError)}
                helperText={
                  idCardTypeError ||
                  userCreateStore.warnings["idCardType"]?.message
                }
                label={langStore.UserIdCardTypeLabel}
                options={idCardTypeOptions}
                nullText={langStore.optionalFieldHint}
                selectedValue={
                  idCardType
                    ? {
                        id: idCardType,
                        displayId:
                          idCardTypeOptions.find(
                            (o: { id: string }) => o.id === idCardType
                          )?.displayId || idCardType,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onIdCardTypeChange(
                    (value?.id as IdCardType | "") || ("" as IdCardType | "")
                  )
                }
              />

              <InputStringMask
                value={idCard}
                error={Boolean(idCardError)}
                warning={
                  !idCardError && Boolean(userCreateStore.warnings["idCard"])
                }
                helperText={
                  idCardError || userCreateStore.warnings["idCard"]?.message
                }
                label={langStore.UserIdCardLabel}
                onChange={(value: string) => onIdCardChange(value)}
                conditionalMasks={[
                  {
                    mask: "999999999",
                    masks: ["999999999", "99999999", "9999999"],
                    when: {
                      field: "idCardType",
                      operator: "in",
                      values: ["dni"],
                    },
                  },
                  {
                    mask: "99-99999999-9",
                    masks: ["99-99999999-9", "99-9999999-9"],
                    when: {
                      field: "idCardType",
                      operator: "in",
                      values: ["cuitCuil"],
                    },
                  },
                ]}
                conditionValues={{ idCardType: idCardType }}
                nullText={langStore.optionalFieldHint}
                description={langStore.UserIdCardDescription}
              />
              <InputReference
                id="gender"
                error={Boolean(genderError)}
                helperText={
                  genderError || userCreateStore.warnings["gender"]?.message
                }
                label={langStore.UserGenderLabel}
                options={genderOptions}
                nullText={langStore.optionalFieldHint}
                selectedValue={
                  gender
                    ? {
                        id: gender,
                        displayId:
                          genderOptions.find(
                            (o: { id: string }) => o.id === gender
                          )?.displayId || gender,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onGenderChange(
                    (value?.id as Gender | "") || ("" as Gender | "")
                  )
                }
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
